import omit from "lodash/omit";
import pick from "lodash/pick";
import upperFirst from "lodash/upperFirst";

import ChangeList from "components/LogItem/elements/ChangeList";
import { flattenChanges } from "components/LogItem/messages/changes";

// The card header already carries the record's identity and timestamps, so the
// snapshot's copies of them add nothing.
const HIDDEN_INITIAL_VALUE_KEYS = [
  "id",
  "created_at",
  "updated_at",
  "deleted_at",
];

// Per record type: links the card's summary sentence already resolves, and
// machine payloads that stay unreadable however they are formatted.
const HIDDEN_INITIAL_VALUE_KEYS_BY_TYPE = {
  // The Google Places payload in info repeats the address columns.
  Address: ["geog", "model_id", "model_type", "info"],
  // The submitted answers open from the card's "View Response" action instead.
  CampaignTargetFormResponse: [
    "info",
    "entity_id",
    "contact_id",
    "campaign_id",
    "campaign_target_email_id",
    "email_url_id",
    "email_form_id",
  ],
  // info and conditions hold the dialer's settings and contact filter rules.
  DialSession: [
    "info",
    "conditions",
    "campaign_id",
    "creator_user_id",
    "voicemail_id",
    "form_id",
  ],
  EmailTemplate: ["info.design"],
  NoteTemplate: ["editor_state"],
};

const formatSnapshotValue = value =>
  Array.isArray(value) ? value.join(", ") : value;

const isScalar = value => value === null || typeof value !== "object";

// Only a list of scalars collapses to one line. A JSONB list of objects
// (DialSession.conditions, Product.versions) would join to "[object Object]".
const isScalarList = value => Array.isArray(value) && value.every(isScalar);

// A JSONB value holding only empty members has nothing to show at any depth.
const isBlank = value =>
  value === null ||
  value === undefined ||
  value === "" ||
  (typeof value === "object" && Object.values(value).every(isBlank));

const humanizeKey = key => upperFirst(String(key).replace(/_/g, " "));

const formatLeaf = value => `'${formatSnapshotValue(value)}'`;

const isLeaf = value => isScalar(value) || isScalarList(value);

const visibleEntries = object =>
  Object.entries(object).filter(([, value]) => !isBlank(value));

// Semantic's list styles outrank the utilities layer, as in ChangeList.
const NESTED_LIST_CLASSES = "tw:my-0! tw:pl-5!";

function renderNestedEntry(key, value) {
  if (isLeaf(value)) {
    return `${humanizeKey(key)}: ${formatLeaf(value)}`;
  }
  return (
    <>
      {`${humanizeKey(key)}:`}
      {renderNestedValue(value)}
    </>
  );
}

// A list member made only of scalars reads on one line, like a table row.
function renderListMember(member) {
  if (isLeaf(member)) {
    return formatLeaf(member);
  }
  const entries = Array.isArray(member) ? [] : visibleEntries(member);
  if (entries.length && entries.every(([, value]) => isLeaf(value))) {
    return entries
      .map(([key, value]) => `${humanizeKey(key)}: ${formatLeaf(value)}`)
      .join(", ");
  }
  return renderNestedValue(member);
}

function renderNestedValue(value) {
  if (Array.isArray(value)) {
    return (
      <ol className={`${NESTED_LIST_CLASSES} tw:list-decimal`}>
        {value
          .filter(member => !isBlank(member))
          .map((member, index) => (
            <li key={index}>{renderListMember(member)}</li>
          ))}
      </ol>
    );
  }
  return (
    <ul className={`${NESTED_LIST_CLASSES} tw:list-disc`}>
      {visibleEntries(value).map(([key, member]) => (
        <li key={key}>{renderNestedEntry(key, member)}</li>
      ))}
    </ul>
  );
}

// Association snapshots arrive as arrays of stringified members, so each member
// gets its own "Added" line rather than one joined value.
const INITIAL_VALUE_ATTRIBUTES = {
  tags: { label: "tag", isCollection: true },
  addresses: { label: "address", isCollection: true },
  websites: { label: "website", isCollection: true },
  attachments: { label: "attachment", isCollection: true },
  custom_fields: {
    mapLine: ({ action, value }) =>
      `${action} custom field '${value.field}' to '${formatSnapshotValue(
        value.value
      )}'`,
  },
};

export function splitInitialValues(initialValues) {
  const changes = [];
  const blobs = [];

  Object.entries(initialValues || {}).forEach(([attribute, value]) => {
    if (HIDDEN_INITIAL_VALUE_KEYS.includes(attribute)) {
      return;
    }
    if (attribute === "custom_fields") {
      Object.entries(value).forEach(([field, fieldValue]) =>
        changes.push({ attribute, value: { field, value: fieldValue } })
      );
    } else if (INITIAL_VALUE_ATTRIBUTES[attribute]?.isCollection) {
      changes.push({ attribute, added: value });
    } else if (!isScalar(value) && isBlank(value)) {
      return;
    } else if (isScalarList(value)) {
      changes.push({ attribute, value: value.join(", ") });
    } else if (!isScalar(value)) {
      // A JSONB column has no readable single-line form, so it renders nested.
      blobs.push({ attribute, value });
    } else {
      changes.push({ attribute, value });
    }
  });

  return { changes, blobs };
}

// The call card prints the status, direction, number and id in its own body, so
// its snapshot lists only the columns those lines leave out.
export const CALL_SNAPSHOT_KEYS = [
  "duration",
  "dial_session_id",
  "is_skipped",
  "agent_hungup",
  "status_verified",
];

// The values a record was created with, phrased through the same formatter the
// "changed" logs use so both kinds of card read alike. Pass `keys` to list a
// subset, for a card that already shows part of the snapshot itself.
export function renderInitialValues(
  initialValues,
  { keys, auditableType } = {}
) {
  const visibleValues = omit(
    initialValues,
    HIDDEN_INITIAL_VALUE_KEYS_BY_TYPE[auditableType] || []
  );
  const { changes, blobs } = splitInitialValues(
    keys ? pick(visibleValues, keys) : visibleValues
  );
  const lines = flattenChanges(changes, INITIAL_VALUE_ATTRIBUTES);

  if (!lines.length && !blobs.length) {
    return null;
  }

  return (
    <ChangeList>
      {lines.map((line, index) => (
        <li key={index}>{line}</li>
      ))}
      {blobs.map(({ attribute, value }) => (
        <li key={attribute}>
          {`Set ${attribute.replace(/_/g, " ")}:`}
          {renderNestedValue(value)}
        </li>
      ))}
    </ChangeList>
  );
}
